<template>
  <div class="full-page-map-wrapper">
    <div class="vue-map-search">
      <GmapAutocomplete
        select-first-on-enter
        class="form-control input-lg"
        placeholder="Search countries, states or cities"
        @place_changed="onSearch"
        @click="clearSelected"
      />
    </div>
    <div>
      <AppMap
        :zoom-level="zoom"
        :viewport="viewport"
        :options="mapOptions"
        :fit-within="fitWithin"
        :map-center-position="center"
        :fit-marker-with-radius="fitMarkerWithRadius"
        :center-on-marker-and-fit-subset="fitAndCenterSubset"
        @dragend="onDragEnd"
        @click="clearSelected"
        @zoom-changed="onZoomChange"
        @current-marker="setCurrentMarker"
        @location-changed="onPositionChange"
        @view-port-adjustment="onViewPortAdjustment"
        @detailed-zoom-threshold="onDetailedZoomThresholdChange"
      >
        <!-- Custom map control slot -->
        <template
          v-if="hasMemberMarkers || hasJobSiteMarkers"
          #customMapControl
        >
          <ActivitiesLocationResetZoomControl @click="onZoomReset" />
        </template>

        <!-- Default slot -->
        <template #default="{ map }">
          <template v-if="viewPortReady && showingRoutes">
            <template v-if="showMemberRoute">
              <GmapPolyline :options="polylineOptions" />
            </template>
            <ActivitiesLocationStartMarker
              v-if="showMemberRoute && selectedMemberStartMarker"
              :size="secondaryIconSize"
              :marker="selectedMemberStartMarker"
            />
            <template v-if="showMoveMarkers">
              <ActivitiesLocationMoveMarker
                v-for="(marker, index) in memberRouteMoveMarkers"
                :key="`move-marker-${marker.user_id}-${index}`"
                :marker="marker"
                :size="secondaryIconSize"
                :show-radius="activeMoveMarker === index"
                :show-info-window="activeMoveMarker === index"
                @close-info="activeMoveMarker = null"
                @info-window="activeMoveMarker = index"
              />
            </template>
          </template>

          <template v-if="currentLocationMarker">
            <!-- Current location marker -->
            <AppMapMarker
              has-icon
              :z-index="1"
              :icon-size="secondaryIconSize"
              :marker="currentLocationMarker"
              class="app-map-current-location-marker"
              :icon-url="defaultCurrentLocationIconUrl"
            />
          </template>

          <template v-if="hasMemberMarkers">
            <ActivitiesLocationAvatarMarker
              v-for="marker in trackedMemberMarkers"
              :key="`avatar-marker-${marker.id}`"
              :map="map"
              :marker="marker"
              :size="primaryIconSize(52, 52)"
              :selected="isSelectedMember(marker.id)"
              :show-radius="isSelectedMember(marker.id)"
              :active-route="isActiveMemberRoutes(marker.id)"
              :show-info-window="showMemberInfoWindow(marker.id)"
              @view-route="onViewMemberRoute"
              @close-route="onCloseRoute"
              @info-window="onAvatarInfoWindow"
              @close-info="onCloseInfoWindow"
              @click="onSelectMemberClick(marker)"
              @select-site="onSelectSiteClick"
            />
          </template>

          <template v-if="hasJobSiteMarkers">
            <AppMapCluster
              v-slot="{ state: { hover, selected, marker }, deselect }"
              :map="map"
              :markers="jobSiteMarkers"
              :marker-size="{ width: 34, height: 44 }"
              :external-selected-marker-id="externalJobSiteMarkerId"
              @marker-click="onSelectSiteClick($event.id)"
            >
              <!--
              NOTE: This is a ghost marker (doesn't render the pin) the actual one is rendered by the cluster.
              We use this one still get the info window, tooltip, etc.
             -->
              <ActivitiesLocationJobSiteMarker
                v-if="marker"
                :key="`job-site-marker-${marker.id}`"
                :marker="marker"
                :map="map"
                :show-marker="false"
                :is-clickable="false"
                :show-radius="selected"
                :show-marker-tooltip="hover"
                :show-info-window="showingInfoWindow && selected"
                @close-info="onCloseInfoWindow"
                @deselect-marker="
                  clearSelected();
                  deselect();
                "
                @click-info-window="onJobSiteInfoWindowClick"
              />
            </AppMapCluster>
          </template>
        </template>
      </AppMap>
    </div>
  </div>
</template>

<script>
import { get, find, isEqual } from 'lodash-es';
import { isMobile, primaryIconSize } from '~/mixins';
import { mapState, mapGetters, mapActions } from 'vuex';
import { ICON_MARKER_CURRENT_LOCATION } from '~/rails_assets';
import { LOCATION_VIEW, LOCATION_MAP_OPTIONS, LOCATION_SITES_MODE } from '~/app_constants';

const MAX_ZOOM_LEVEL = 18;
const POLYLINE_STYLE = Object.freeze({
  strokeColor: '#2aa7ff',
  strokeOpacity: 1.0,
  strokeWeight: 3,
});
const DEFAULT_ZOOM_LEVEL = 3;
const DETAILED_ZOOM_THRESHOLD = 13;

export default {
  name: 'ActivitiesLocationMap',
  mixins: [isMobile, primaryIconSize],
  props: {
    isDashboardMap: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  data() {
    return {
      viewport: null,
      currentLocationMarker: null,
      defaultCurrentLocationIconUrl: ICON_MARKER_CURRENT_LOCATION,
      defaultIconSize: { width: 15, height: 15 },
      fitWithin: [],
      fitMarkerWithRadius: null,
      viewPortReady: true,
      detailedLocationData: false,
      detailedZoomThreshold: DETAILED_ZOOM_THRESHOLD,

      fitAndCenterSubset: null,
      adjustedUsersWithin: 0,
      activeMoveMarker: null,
      zoom: DEFAULT_ZOOM_LEVEL,
      center: { lat: 0, lng: 0 },
      mapOptions: Object.freeze(LOCATION_MAP_OPTIONS),
    };
  },
  computed: {
    ...mapGetters([
      'sites',
      'isPastView',
      'siteMarkers',
      'hasMemberMarkers',
      'hasJobSiteMarkers',
      'selectedSiteMarker',
      'selectedMemberMarker',
      'siteMarkersByPartition',
      'memberMarkersByPartition',
      'memberMarkersWithRecentLocations',
    ]),
    ...mapState([
      'users',
      'loading',
      'currentView',
      'selectedSite',
      'selectedMember',
      'sitesViewMode',
      'showingRoutes',
      'showingInfoWindow',
      'showLocationPoints',
    ]),
    ...mapState({ debugMode: 'debug' }),
    externalJobSiteMarkerId() {
      return this.selectedSiteMarker ? this.selectedSiteMarker.id : null;
    },
    trackedMemberMarkers() {
      const { tracked } = this.memberMarkersByPartition;

      return this.isDashboardMap ? this.memberMarkersWithRecentLocations : tracked;
    },
    jobSiteMarkers() {
      const { visited } = this.siteMarkersByPartition;
      const sites = this.sitesViewMode === LOCATION_SITES_MODE.VISITED ? visited : this.siteMarkers;

      return sites || [];
    },
    showMemberRoute() {
      return this.showingRoutes && !!this.selectedMemberRoute.length;
    },
    selectedMemberRoute() {
      return get(this.selectedMemberMarker, 'route', []);
    },
    polylineOptions() {
      return { ...POLYLINE_STYLE, path: this.memberRoutePositions, zIndex: 1 };
    },
    memberRoutePositions() {
      const routePositions = this.selectedMemberRoute.map(({ position }) => position);
      const { position: currentPosition } = this.selectedMemberMarker || {};

      return currentPosition ? [...routePositions, currentPosition] : routePositions;
    },
    memberRouteMoveMarkers() {
      return this.selectedMemberRoute.length > 1 ? this.selectedMemberRoute.slice(1) : this.selectedMemberRoute;
    },
    selectedMemberStartMarker() {
      return this.selectedMemberRoute.length > 1 ? this.selectedMemberRoute[0] : null;
    },
    showMoveMarkers() {
      return this.showMemberRoute && (this.detailedLocationData || (this.showLocationPoints > 0 && this.debugMode));
    },
    secondaryIconSize() {
      return !(this.zoom > DETAILED_ZOOM_THRESHOLD)
        ? this.defaultIconSize
        : { width: this.zoom, height: this.zoom };
    },
  },
  watch: {
    'loading.users'(newValue, oldValue) {
      if (newValue) {
        return;
      }

      this.adjustInitialFitWithin();
    },
    currentView(newView) {
      if (newView === LOCATION_VIEW.LIVE) {
        this.adjustedUsersWithin = 0;
      }
    },
    selectedSiteMarker(newSiteMarker, oldSiteMarker) {
      // Avoid map "recentering" when we're on live mode and we
      // have a member marker selected. Otherwise the info window
      // will be out of place again.
      if (newSiteMarker && !isEqual(newSiteMarker, oldSiteMarker)) {
        const userTriggered = !oldSiteMarker ? true : newSiteMarker.id !== oldSiteMarker.id;

        this.updateMapCenterAndZoomIn(newSiteMarker, userTriggered);
      }
    },
    selectedMemberMarker(newMemberMarker, oldMemberMarker) {
      // Avoid map "recentering" when we're on live mode and we
      // have a member marker selected. Otherwise the info window
      // will be out of place again.
      if (newMemberMarker && !isEqual(newMemberMarker, oldMemberMarker)) {
        const userTriggered = !oldMemberMarker ? true : newMemberMarker.id !== oldMemberMarker.id;

        // Only adjust the map when the user is in the group of
        // members that have tracked location for the given date.
        // Otherwise we would be trying to re-center on an
        // inexistent marker in the map.
        if (newMemberMarker.trackedLocation) {
          this.updateMapCenterAndZoomIn(newMemberMarker, userTriggered);
        }
      }
    },
    async showingRoutes(newValue) {
      if (newValue) {
        await this.loadUserRoute(get(this.selectedMemberMarker, 'id'));
        this.fitAndCenterSubset = {
          markersSubset: this.memberRoutePositions.map((position) => ({ position })),
          mainMarker: this.selectedMemberMarker,
        };

        // Reset fitWithin in order to get changes again
        // if we need to fit whithin previous route values.
        await this.$nextTick();
        this.fitAndCenterSubset = null;
      } else {
        // If we're hiding the routes but we still have a selected
        // member from the top grouping of the sidebar; meaning the
        // member has tracked location for the specified date; we want
        // to recenter the map and zoom towards the user.
        const trackedLocation = get(this.selectedMemberMarker, 'trackedLocation', false);

        if (this.selectedMemberMarker && trackedLocation) {
          this.updateMapCenterAndZoomIn(this.selectedMemberMarker, true);
        }
      }
    },
    zoom(newValue, oldValue) {
      const zoomThreshold = Math.max(DETAILED_ZOOM_THRESHOLD, this.detailedZoomThreshold);

      this.detailedLocationData = newValue > zoomThreshold || newValue === MAX_ZOOM_LEVEL;
    },
    detailedZoomThreshold(newValue, oldValue) {
      const zoomThreshold = Math.max(DETAILED_ZOOM_THRESHOLD, newValue);

      this.detailedLocationData = this.zoom > zoomThreshold || this.zoom === MAX_ZOOM_LEVEL;
    },
    sitesViewMode(newValue, oldValue) {
      if (!this.selectedMember && !this.showingInfoWindow && !this.showingRoutes) {
        this.adjustFitWithin();
      }
    },
  },
  methods: {
    ...mapActions([
      'showRoutes',
      'selectSite',
      'selectMember',
      'clearSelected',
      'showInfoWindow',
      'loadUserRoute',
      'setCurrentView',
      'setSitesViewMode',
    ]),
    onZoomChange(zoomLevel) {
      this.zoom = Math.min(zoomLevel, MAX_ZOOM_LEVEL);
    },
    onDetailedZoomThresholdChange(detailedZoomThreshold) {
      this.detailedZoomThreshold = detailedZoomThreshold;
    },
    onViewPortAdjustment(adjustmentStatus) {
      this.viewPortReady = adjustmentStatus === 'ready';
    },
    onJobSiteInfoWindowClick(marker) {
      this.onSelectMemberClick(marker);
      this.onAvatarInfoWindow(marker.user_id);
    },
    onSelectMemberClick(marker, userTriggered = true) {
      const user = this.users[marker.id];

      if (user && user.id !== get(this.selectedMember, 'id')) {
        this.clearSelected();
        this.selectMember(user);
      }
    },
    async onSelectSiteClick(siteId) {
      const selectedSiteId = get(this.selectedSite, 'id');
      const jobSite = find(this.sites, { id: siteId });

      if (jobSite && jobSite.id !== selectedSiteId) {
        if (!jobSite.visited) {
          this.setSitesViewMode(LOCATION_SITES_MODE.ALL);
          await this.$nextTick();
        }

        this.clearSelected();
        this.selectSite(jobSite);
      } else if (jobSite && jobSite.id === selectedSiteId && !this.showingInfoWindow) {
        this.showInfoWindow(true);
      }
    },
    isSelectedMember(userId) {
      return get(this.selectedMember, 'id') === userId;
    },
    showSiteRadius(siteId) {
      return get(this.selectedSite, 'id') === siteId;
    },
    isActiveMemberRoutes(userId) {
      return this.showingRoutes && this.isSelectedMember(userId);
    },
    showSiteInfoWindow(markerId) {
      const isSelectedSite = get(this.selectedSite, 'id') === markerId;

      return this.showingInfoWindow && isSelectedSite;
    },
    showMemberInfoWindow(userId) {
      return this.showingInfoWindow && this.isSelectedMember(userId);
    },
    onViewMemberRoute(userId) {
      this.showRoutes(this.isSelectedMember(userId));
    },
    onAvatarInfoWindow(userId) {
      this.showInfoWindow(this.isSelectedMember(userId));
    },
    onCloseRoute() {
      this.showRoutes(false);
    },
    onCloseInfoWindow() {
      this.showInfoWindow(false);
    },
    onDragEnd() {
      this.center = null;
    },
    onSearch(place) {
      if (place) {
        const {
          geometry: { location },
        } = place;

        this.center = {
          lat: location.lat(),
          lng: location.lng(),
        };
        this.viewport = place.geometry.viewport;
      }
    },
    onPositionChange(position, zoomLevel) {
      this.center = position;
      this.onZoomChange(zoomLevel);
    },
    adjustFitWithin() {
      let locationsToFitWithin = [];

      if (this.sitesViewMode === LOCATION_SITES_MODE.ALL) {
        if (this.trackedMemberMarkers.length > 0) {
          // The markers array is likely to be bigger than the job sites array
          // thus we use it to initialize the Set object
          const locationData = new Set(this.trackedMemberMarkers);

          this.siteMarkers.forEach((item) => locationData.add(item));
          locationsToFitWithin = Array.from(locationData);
        } else {
          locationsToFitWithin = this.siteMarkers;
        }
      } else {
        // If there are NO markers we still center and zoom the map into the existing non-visited job sites...
        // ... even though they are not displayed
        locationsToFitWithin = this.trackedMemberMarkers.length > 0 ? this.trackedMemberMarkers : this.siteMarkers;
      }

      // we clear the contents of this.fitWithin and assign the new value on the next tick ...
      // ... so that the zooming and centering take place as expected
      this.fitWithin = [];
      this.$nextTick(() => {
        this.fitWithin = locationsToFitWithin;
      });
    },
    adjustInitialFitWithin() {
      // Only adjust within on first "load" or when user
      // switches back to live view and we end loading
      // "current data" again or if we're in the dashboard
      // widget and we need to readjust the zoom and fit.
      const hasNotBeenAdjusted = this.adjustedUsersWithin === 0;
      const isPastView = this.currentView === LOCATION_VIEW.PAST;

      if (hasNotBeenAdjusted || isPastView || this.isDashboardMap) {
        if (!this.selectedMember) {
          this.adjustFitWithin();
          this.adjustedUsersWithin += 1;
        }
      }
    },
    setCurrentMarker(position) {
      this.clearSelected();
      this.currentLocationMarker = position || null;
    },
    onZoomReset() {
      this.clearSelected();
      this.adjustFitWithin();
    },
    async updateMapCenterAndZoomIn(marker, userTriggered = false) {
      if (userTriggered) {
        await this.$nextTick();
        this.fitMarkerWithRadius = marker;

        await this.$nextTick();
        this.fitMarkerWithRadius = null;
      } else {
        await this.$nextTick();
        this.center = marker.position;
      }
    },
  },
};
</script>
